/**
 * A service class for managing table objects and their API instances.
 */
class TableObjectAPIService {
    /**
     * Constructs the TableObjectAPIService.
     * @param {Object} $q - AngularJS promise service.
     */
    constructor($q) {
        this.$q = $q;
        this.registeredTableObjects = []; // List of registered table objects.
        this.unresolvedPromises = []; // List of unresolved promises for table objects.
    }

    /**
     * Builds an API instance for a given table object data.
     * @param {Object} tableObjectData - The data of the table object.
     * @returns {Object|boolean} - The API instance or false if the input is not an object.
     */
    buildApiInstance(tableObjectData) {
        if (!angular.isObject(tableObjectData)) { return false; }

        return {
            /**
             * Gets the number of rows in the table object.
             * @returns {number|undefined} - The number of rows or undefined if not available.
             */
            getNumRows: function () { return tableObjectData.rows || undefined; },

            /**
             * Gets the number of columns in the table object.
             * @returns {number|undefined} - The number of columns or undefined if not available.
             */
            getNumCols: function () { return tableObjectData.cols || undefined; },

            /**
             * Gets the maximum number of rows allowed in the table object.
             * @returns {number|undefined} - The maximum number of rows or undefined if not available.
             */
            getMaxNumRows: function () { return tableObjectData.maxRows || undefined; },

            /**
             * Gets the maximum number of columns allowed in the table object.
             * @returns {number|undefined} - The maximum number of columns or undefined if not available.
             */
            getMaxNumCols: function () { return tableObjectData.maxCols || undefined; },

            /**
             * Reads the value of a cell in the table object.
             * @param {number} row - The row index.
             * @param {number} col - The column index.
             * @returns {*} - The value of the cell.
             */
            readCell: function (row, col) { return tableObjectData.readCell(row, col); },

            /**
             * Writes a value to a cell in the table object.
             * @param {number} row - The row index.
             * @param {number} col - The column index.
             * @param {*} value - The value to write.
             * @returns {*} - The result of the write operation.
             */
            writeCell: function (row, col, value) { return tableObjectData.writeCell(row, col, value); },

            /**
             * Sets a cell in the table object to read-only.
             * @param {number} row - The row index.
             * @param {number} col - The column index.
             * @param {boolean} readonly - Whether the cell should be read-only.
             * @returns {*} - The result of the set operation.
             */
            setCellReadOnly: function (row, col, readonly) { return tableObjectData.setCellReadOnly(row, col, Boolean(readonly)); },

            /**
             * Adds a row to the table object.
             * @returns {*} - The result of the add operation.
             */
            addRow: function () { return tableObjectData.addRow(); },

            /**
             * Removes a row from the table object.
             * @returns {*} - The result of the remove operation.
             */
            removeRow: function () { return tableObjectData.removeRow(); },

            /**
             * Adds a column to the table object.
             * @returns {*} - The result of the add operation.
             */
            addCol: function () { return tableObjectData.addCol(); },

            /**
             * Removes a column from the table object.
             * @returns {*} - The result of the remove operation.
             */
            removeCol: function () { return tableObjectData.removeCol(); }
        };
    }

    /**
     * Registers a table object with the service.
     * @param {Object} data - The table object data to register.
     * @returns {boolean} - True if the registration was successful.
     * @throws {Error} - If data or id is missing.
     */
    registerTableObject(data) {
        if (!data || !angular.isDefined(data.id)) {
            throw new Error('Data or Id missing');
        }

        const duplicateEntry = this.registeredTableObjects.findIndex(object => object.id === data.id);
        
        if (duplicateEntry >= 0) {
            this.registeredTableObjects[duplicateEntry] = data;
        } else {
            this.registeredTableObjects.push(data);
        }

        const promise = this.unresolvedPromises.find(object => object.id === data.id);

        if (promise) {
            promise.deferred.resolve(this.buildApiInstance(data));
        }

        return true;
    }

    /**
     * Retrieves a table object by its id.
     * @param {string} id - The id of the table object.
     * @returns {Promise} - A promise that resolves with the table object API instance.
     */
    getTableObject(id) {
        if (!id) { return false; }

        const deferred = this.$q.defer();

        const result = this.registeredTableObjects.find(object => object.id === id);

        if (result) {
            deferred.resolve(this.buildApiInstance(result));
        } else {
            this.unresolvedPromises.push({
                id: id,
                deferred: deferred
            });
        }

        return deferred.promise;
    }

    /**
     * Updates the id of a table object.
     * @param {string} oldId - The old id of the table object.
     * @param {string} newId - The new id for the table object.
     * @returns {boolean} - True if the update was successful, false otherwise.
     * @throws {Error} - If oldId or newId is missing.
     */
    updateTableObject(oldId, newId) {
        if (!oldId || !newId) { throw new Error('Missing ids.'); }
        const checkNewId = this.registeredTableObjects.find(object => object.id === newId);
        if (checkNewId) return false;

        const result = this.registeredTableObjects.find(object => object.id === oldId);
        if (!result) return false;

        result.id = newId;
        return true;
    }
}

export default TableObjectAPIService;